/**
 * Las políticas legales del sitio: privacidad, términos, reembolsos, cancelación.
 *
 * Son documentos del CLIENTE, no de Veltica. Los términos que se aceptan al
 * entrar al panel son otra cosa y no se editan aquí.
 *
 * Lista y ficha, como Reservas, y no cuatro cajas de texto apiladas: son
 * documentos de varias páginas, y tenerlos todos abiertos a la vez obligaba a
 * pasar por encima de mil palabras para llegar al siguiente. La lista dice de
 * un vistazo cuáles faltan —que es la mitad de para qué sirve esta pantalla— y
 * dentro se escribe uno solo, con sitio de sobra.
 *
 * Cuál está abierta viaja en la URL, igual que la ficha de un servicio: así el
 * botón de atrás del navegador hace lo que se espera.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { longDate } from '../../../lib/dates'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBold,
  IconCalendarX,
  IconExternalLink,
  IconFileText,
  IconH1,
  IconH2,
  IconList,
  IconLink,
  IconPilcrow,
  IconReceiptRefund,
  IconScale,
  IconShieldLock,
  IconSparkles,
  IconTruckDelivery,
} from '@tabler/icons-react'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { useToast } from '../../Toast'
import { PROFILE_NOTE_KEY, useRegion } from '../../../lib/region'
import { useI18n } from '../../../lib/i18n'
import { countryLabel } from '../../../lib/displayNames'
import { listProjectPolicies, saveProjectPolicy, type ProjectPolicy } from '../../../lib/api'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { Notice } from '../Notice'
import { ConfirmDialog } from '../ConfirmDialog'

/**
 * Lo que queda por rellenar de una plantilla, para poder avisarlo.
 *
 * La misma regla que `POLICY_PLACEHOLDER` en el worker, que es quien decide si
 * se publica: aquí sólo se usa para avisar mientras se escribe.
 */
const PLACEHOLDER = /\[[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ0-9 ,./%]*\]/g

/** Los datos que faltan, dichos como se llaman en «Información del negocio». */
function fieldList(fields: string[], t: (key: string) => string): string {
  return fields.map((field) => t(`business.${field}.label`).toLowerCase()).join(', ')
}

/** Un icono por documento: de un vistazo se distingue cuál es cuál. */
const ICONS: Record<string, React.ReactNode> = {
  privacidad: <IconShieldLock size={18} />,
  terminos: <IconFileText size={18} />,
  reembolsos: <IconReceiptRefund size={18} />,
  cancelaciones: <IconCalendarX size={18} />,
  envios: <IconTruckDelivery size={18} />,
  'aviso-legal': <IconScale size={18} />,
}

function escapeEditorHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function inlineEditorHtml(value: string): string {
  return escapeEditorHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]/g, '<mark>[$1]</mark>')
}

/** El formato guardado sigue siendo Markdown, pero el editor nunca lo expone. */
function markdownToEditorHtml(source: string): string {
  const out: string[] = []
  let listOpen = false
  const closeList = () => { if (listOpen) { out.push('</ul>'); listOpen = false } }

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) { closeList(); continue }
    if (line.startsWith('## ')) { closeList(); out.push(`<h2>${inlineEditorHtml(line.slice(3))}</h2>`); continue }
    if (line.startsWith('# ')) { closeList(); out.push(`<h1>${inlineEditorHtml(line.slice(2))}</h1>`); continue }
    if (line.startsWith('- ')) {
      if (!listOpen) { out.push('<ul>'); listOpen = true }
      out.push(`<li>${inlineEditorHtml(line.slice(2))}</li>`)
      continue
    }
    closeList()
    out.push(`<p>${inlineEditorHtml(line)}</p>`)
  }
  closeList()
  return out.join('') || '<p><br></p>'
}

function inlineMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/\u00a0/g, ' ')
  if (!(node instanceof HTMLElement)) return ''
  if (node.tagName === 'BR') return '\n'
  const text = Array.from(node.childNodes).map(inlineMarkdown).join('')
  return node.matches('strong, b') && text.trim() ? `**${text}**` : text
}

function editorToMarkdown(root: HTMLElement): string {
  const lines: string[] = []
  for (const block of Array.from(root.children)) {
    if (!(block instanceof HTMLElement)) continue
    if (block.matches('ul, ol')) {
      for (const item of Array.from(block.children)) {
        const text = inlineMarkdown(item).trim()
        if (text) lines.push(`- ${text}`)
      }
      lines.push('')
      continue
    }
    const text = inlineMarkdown(block).trim()
    if (!text) { lines.push(''); continue }
    if (block.tagName === 'H1') lines.push(`# ${text}`, '')
    else if (block.tagName === 'H2') lines.push(`## ${text}`, '')
    else lines.push(text, '')
  }
  return lines.join('\n').trim()
}

function PolicyVisualEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useI18n()
  const editorRef = useRef<HTMLDivElement>(null)
  const emittedRef = useRef(value)

  useEffect(() => {
    if (!editorRef.current || value === emittedRef.current) return
    editorRef.current.innerHTML = markdownToEditorHtml(value)
    emittedRef.current = value
  }, [value])

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = markdownToEditorHtml(value)
    // El primer montaje necesita pintar el documento; los siguientes cambios
    // pasan por el efecto anterior para no mover el cursor mientras se escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = () => {
    if (!editorRef.current) return
    const next = editorToMarkdown(editorRef.current)
    emittedRef.current = next
    onChange(next)
  }

  const command = (name: string, argument?: string) => {
    editorRef.current?.focus()
    document.execCommand(name, false, argument)
    emit()
  }

  const paragraph = () => {
    editorRef.current?.focus()
    if (document.queryCommandState('insertUnorderedList')) document.execCommand('insertUnorderedList')
    document.execCommand('formatBlock', false, 'p')
    emit()
  }

  return (
    <div className="dash-policy-composer">
      <div className="dash-policy-toolbar" role="toolbar" aria-label={t('policies.toolbar')}>
        <button type="button" title={t('policies.format.paragraph')} onMouseDown={(event) => { event.preventDefault(); paragraph() }}><IconPilcrow size={15} /><span>{t('policies.format.paragraph')}</span></button>
        <button type="button" title={t('policies.format.h1')} onMouseDown={(event) => { event.preventDefault(); command('formatBlock', 'h1') }}><IconH1 size={15} /><span>{t('policies.format.h1Short')}</span></button>
        <button type="button" title={t('policies.format.h2')} onMouseDown={(event) => { event.preventDefault(); command('formatBlock', 'h2') }}><IconH2 size={15} /><span>{t('policies.format.h2')}</span></button>
        <button type="button" title={t('policies.format.bold')} onMouseDown={(event) => { event.preventDefault(); command('bold') }}><IconBold size={15} /><span>{t('policies.format.bold')}</span></button>
        <button type="button" title={t('policies.format.list')} onMouseDown={(event) => { event.preventDefault(); command('insertUnorderedList') }}><IconList size={15} /><span>{t('policies.format.list')}</span></button>
      </div>
      <div
        ref={editorRef}
        className="dash-policy-editor-text"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={t('policies.editorAria')}
        spellCheck
        onInput={emit}
        suppressContentEditableWarning
      />
    </div>
  )
}

/**
 * El interruptor de «Automática», el mismo en la lista y en la ficha.
 *
 * Siempre a la vista, encendido o apagado: las cuatro nacen automáticas y es
 * lo que el dueño tiene que saber de cada una antes que nada. Apagarlo hace
 * suya la versión que ve —el texto generado se guarda como propio— para que
 * no empiece de una página en blanco.
 */
function AutoSwitch({ policy, busy, onChange }: {
  policy: ProjectPolicy
  busy: boolean
  onChange: (on: boolean) => void
}) {
  const { t } = useI18n()
  return (
    <label className="dash-policy-auto-switch">
      <span>{t('policies.autoSwitch')}</span>
      <button
        type="button"
        className={`dash-general-toggle${policy.auto ? ' is-on' : ''}`}
        role="switch"
        aria-checked={policy.auto}
        aria-label={t('policies.autoSwitchAria', { title: policy.title })}
        disabled={busy}
        onClick={() => onChange(!policy.auto)}
      >
        <span />
      </button>
    </label>
  )
}

/** Si una política está apagada y sin texto: no se publica por eso. */
const isPending = (policy: ProjectPolicy) => !policy.auto && !policy.body.trim()

export function PoliciesSection({ projectId, token, siteOrigin, onBack, record, onRecordChange, onOpenRegion, onOpenBusiness }: {
  projectId: string
  token: string
  /** Vuelta al índice de Configuración, de donde se abre esta pantalla. */
  onBack?: (() => void) | undefined
  /** Origen del sitio publicado. Vacío mientras no se haya publicado. */
  siteOrigin?: string | null | undefined
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  /** A donde se cambia el país, que es lo que decide qué plantilla se ofrece. */
  onOpenRegion?: (() => void) | undefined
  /** Donde se ponen los datos que le faltan a una política para publicarse. */
  onOpenBusiness?: (() => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, language } = useI18n()
  const { countryCode, countryName, profile, language: policyLanguage } = useRegion()
  const [policies, setPolicies] = useState<ProjectPolicy[]>([])
  const [loading, setLoading] = useState(true)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const policiesLoader = useRecordLoader(loading)
  const [failed, setFailed] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPolicies(await listProjectPolicies(token, projectId))
      setFailed('')
    } catch (error) {
      setFailed(error instanceof Error ? error.message : t('policies.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, token, t])

  useEffect(() => { void load() }, [load])

  const open = policies.find((row) => row.key === record) ?? null

  /**
   * Una clave que no corresponde a nada vuelve a la lista.
   *
   * Pasa con un enlace viejo: sin esto la pantalla se quedaba en blanco sin
   * decir por qué. Mismo criterio que la ficha de un servicio.
   */
  useEffect(() => {
    if (loading || failed || !record) return
    if (!policies.some((row) => row.key === record)) onRecordChange?.(null)
  }, [record, policies, loading, failed, onRecordChange])

  /** El enlace público de una política, al portapapeles. */
  const copyLink = async (policy: ProjectPolicy) => {
    const url = `${siteOrigin ?? ''}${policy.path}`
    try {
      await navigator.clipboard.writeText(url)
      showToast(t('common.linkCopied'))
    } catch {
      showToast(t('common.copyFailed'), 'error')
    }
  }

  const applySaved = (saved: ProjectPolicy) => {
    setPolicies((rows) => rows.map((row) => (row.key === saved.key ? saved : row)))
  }

  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [confirmAutoKey, setConfirmAutoKey] = useState<ProjectPolicy['key'] | null>(null)

  /**
   * El interruptor desde la lista.
   *
   * Apagar guarda como propio el texto que se ve y abre la ficha para
   * escribirlo. Encender una que ya tiene texto propio lo pierde, así que eso
   * se confirma; una pendiente, vacía, se enciende sin preguntar.
   */
  const setAuto = async (policy: ProjectPolicy, on: boolean) => {
    if (on && !isPending(policy)) {
      setConfirmAutoKey(policy.key)
      return
    }
    setBusyKey(policy.key)
    try {
      const saved = await saveProjectPolicy(token, projectId, policy.key, on ? { auto: true } : { body: policy.body })
      applySaved(saved)
      showToast(on ? t('policies.autoOn') : t('policies.autoOff'))
      if (!on) onRecordChange?.(policy.key)
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('policies.saveFailed'), 'error')
    } finally {
      setBusyKey(null)
    }
  }

  const confirmAuto = async () => {
    const key = confirmAutoKey
    if (!key) return
    setBusyKey(key)
    try {
      applySaved(await saveProjectPolicy(token, projectId, key, { auto: true }))
      showToast(t('policies.autoOn'))
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('policies.saveFailed'), 'error')
    } finally {
      setBusyKey(null)
      setConfirmAutoKey(null)
    }
  }

  if (open) {
    return (
      <PolicyEditor
        policy={open}
        projectId={projectId}
        token={token}
        siteOrigin={siteOrigin ?? ''}
        onBack={() => onRecordChange?.(null)}
        onSaved={applySaved}
        onToast={showToast}
        onOpenBusiness={onOpenBusiness}
      />
    )
  }

  /* Pendiente sólo es la apagada sin texto: las demás existen, automáticas o
     escritas. Si se publican o no depende de que no les falten datos, y eso lo
     dice el servidor. */
  const written = policies.filter((row) => !isPending(row)).length
  const languageName = t(`language.${policyLanguage}`)

  /* La espera sustituye a la pantalla ENTERA, título y descripción incluidos:
     estando dentro de la tarjeta, la cabecera se pintaba sobre un hueco y luego
     se recolocaba al llegar el contenido. Ver `SectionLoader`. */
  if (policiesLoader.visible) return <SectionLoader loader={policiesLoader} label={t('policies.loading')} />

  return (
    <div className="dash-dashboard-view dash-settings-view dash-policies-settings dash-loaded-content-in">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro">
          {onBack ? (
            <button type="button" className="dash-secondary-btn dash-settings-back" onClick={onBack}>
              <IconArrowLeft size={14} /> {t('config.back')}
            </button>
          ) : null}
          <div className="dash-header" style={{ margin: 0 }}>
            <h1>{t('policies.title')} · {languageName}</h1>
            <p>
              {t('policies.intro')}
            </p>
            {/* El idioma de las políticas es el del SITIO —Región › Idioma—,
                no el del panel: son páginas que leen sus clientes. */}
            <p className="dash-field-hint">
              {t('policies.languageNoteBefore', { language: languageName })}{' '}
              {onOpenRegion
                ? <button type="button" className="dash-inline-link" onClick={onOpenRegion}>{t('policies.languageLink')}</button>
                : <strong>{t('policies.languageLink')}</strong>}.
            </p>
          </div>
        </header>

        <div className="dash-settings-content">
          {failed ? (
            <div className="dash-placeholder">
              <strong>{t('policies.loadFailedTitle')}</strong>
              <span>{failed}</span>
              <button type="button" className="dash-secondary-btn" onClick={() => void load()}>{t('policies.retry')}</button>
            </div>
          ) : (
            <div className="dash-account-grid">
              {written < policies.length ? (
                <Notice
                  title={written === 0
                    ? t('policies.noneTitle')
                    : policies.length - written === 1
                      ? t('policies.pendingOne')
                      : t('policies.pendingMany', { count: policies.length - written })}
                >
                  <p>{written === 0 ? t('policies.noneBody') : t('policies.pendingBody')}</p>
                </Notice>
              ) : null}

              {policies.map((policy) => {
                const exists = !isPending(policy)
                const live = policy.published
                const holes = policy.body.match(PLACEHOLDER)?.length ?? 0
                return (
                  <section className="dash-settings-card is-raised is-openable" key={policy.key}>
                    <div className="dash-settings-card-head">
                      <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
                        {ICONS[policy.key] ?? <IconFileText size={18} />}
                      </span>
                      <div className="dash-settings-card-head-copy">
                        <h3>{policy.title}</h3>
                        <p>{t(policy.hintKey)}</p>
                      </div>
                      <span className={`dash-connect-status is-${live ? 'ok' : 'warn'}`}>
                        {live ? t('policies.live') : exists ? t('policies.unpublished') : t('policies.draft')}
                      </span>
                    </div>

                    <AutoSwitch policy={policy} busy={busyKey === policy.key} onChange={(on) => void setAuto(policy, on)} />

                    {/* Guardada pero sin publicar: se dice qué le falta y se
                        lleva a donde se arregla. Casi siempre es un dato del
                        negocio —el correo—, no algo que escribir en el texto. */}
                    {exists && !live ? (
                      <p className="dash-policy-pending">
                        <IconAlertTriangle size={14} />
                        {policy.missingFields.length ? (
                          <>
                            {t('policies.missingFields', { fields: fieldList(policy.missingFields, t) })}
                            {onOpenBusiness ? <> · <button type="button" className="dash-inline-link" onClick={onOpenBusiness}>{t('policies.completeBusiness')}</button></> : null}
                          </>
                        ) : holes === 1 ? t('policies.holesOne') : t('policies.holesMany', { count: holes })}
                      </p>
                    ) : null}

                    <div className="dash-policy-card-foot">
                      <span className="dash-field-hint" style={{ margin: 0 }}>
                        {policy.auto
                          ? t('policies.autoFoot')
                          : policy.updatedAt
                            ? t('policies.savedOn', { date: longDate(policy.updatedAt) })
                            : t('policies.willPublishAt', { path: policy.path })}
                      </span>

                      <div className="dash-policy-foot-actions">
                        {/* Copiar y abrir sólo cuando hay algo que abrir: una
                            política sin publicar devuelve el 404 público, y un
                            enlace que lleva ahí es peor que no ofrecerlo. */}
                        {live && siteOrigin ? (
                          /* Sólo el icono: son dos atajos al enlace público, y
                             escritos ocupaban más que «Editar», que es la
                             acción de verdad de la tarjeta. El nombre sigue
                             estando —en el tooltip y para el lector de
                             pantalla—, sólo que no ocupa sitio. */
                          <>
                            <button
                              type="button"
                              className="dash-secondary-btn dash-policy-link-btn"
                              onClick={() => void copyLink(policy)}
                              aria-label={t('policies.copyLink')}
                              data-tooltip={t('policies.copyLink')}
                            >
                              <IconLink size={15} />
                            </button>
                            <a
                              className="dash-secondary-btn dash-policy-link-btn"
                              href={`${siteOrigin}${policy.path}`}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={t('policies.openLink')}
                              data-tooltip={t('policies.openLink')}
                            >
                              <IconExternalLink size={15} />
                            </a>
                          </>
                        ) : null}

                        <button type="button" className="dash-primary-btn dash-new-project-btn" onClick={() => onRecordChange?.(policy.key)}>
                          {exists ? t('policies.edit') : t('policies.write')}
                        </button>
                      </div>
                    </div>
                  </section>
                )
              })}

              {/* Lo que hay que decir siempre que se ofrece una plantilla legal.
                  El país sale del ajuste de región, no de una frase escrita a
                  mano: decirle a alguien que su documento cita una ley que no
                  cita es peor que no decirle nada. */}
              <p className="dash-field-hint">
                {t('policies.disclaimerBefore')} <strong>{countryLabel(countryCode, language, countryName)}</strong>{' '}
                {t('policies.disclaimerCite', { note: t(PROFILE_NOTE_KEY[profile]) })}
                {onOpenRegion ? <> {t('policies.disclaimerChangeIn')} <button type="button" className="dash-inline-link" onClick={onOpenRegion}>{t('section.region')}</button></> : null}.{' '}
                {t('policies.disclaimerAfter')}
              </p>
            </div>
          )}
        </div>
      </div>

      {confirmAutoKey ? (
        <ConfirmDialog
          title={t('policies.restoreAuto.title')}
          message={t('policies.restoreAuto.body')}
          confirmLabel={t('policies.restoreAuto.confirm')}
          onConfirm={confirmAuto}
          onCancel={() => setConfirmAutoKey(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * La ficha de una política: el documento entero, con sitio para escribirlo.
 *
 * A todo el ancho y no en el marco de dos columnas de Configuración: aquí se
 * escribe un documento de varias páginas, y la columna del título le quitaría
 * justo el sitio que hace falta.
 */
function PolicyEditor({ policy, projectId, token, siteOrigin, onBack, onSaved, onToast, onOpenBusiness }: {
  policy: ProjectPolicy
  projectId: string
  token: string
  siteOrigin: string
  onBack: () => void
  onSaved: (saved: ProjectPolicy) => void
  onToast: (message: string, tone?: 'error') => void
  onOpenBusiness?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const [draft, setDraft] = useState(policy.body)
  const [saving, setSaving] = useState(false)
  const [confirmAuto, setConfirmAuto] = useState(false)

  const dirty = draft !== policy.body
  /* Volver con el documento a medio escribir hace parpadear la barra de arriba
     en vez de perderlo. */
  const blockUnsaved = useUnsavedGuard()
  const pending = draft.match(PLACEHOLDER) ?? []
  const exists = !isPending(policy)
  const live = policy.published

  const persist = async (change: { body: string } | { auto: true }) => {
    setSaving(true)
    try {
      const saved = await saveProjectPolicy(token, projectId, policy.key, change)
      onSaved(saved)
      setDraft(saved.body)
      onToast('auto' in change
        ? t('policies.autoOn')
        : policy.auto ? t('policies.autoOff') : saved.body ? t('policies.saved') : t('policies.withdrawn'))
    } catch (error) {
      onToast(error instanceof Error ? error.message : t('policies.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const save = () => persist({ body: draft })

  return (
    <div className="dash-dashboard-view dash-settings-view dash-policies-settings">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro">
          <button type="button" className="dash-secondary-btn dash-settings-back" onClick={() => { if (!blockUnsaved()) onBack() }}>
            <IconArrowLeft size={14} /> {t('policies.back')}
          </button>
          <div className="dash-header" style={{ margin: 0 }}>
            <h1>{policy.title}</h1>
            <p>{t(policy.hintKey)}</p>
          </div>
        </header>

        <div className="dash-settings-content">
          <div className="dash-account-grid">
            <section className="dash-settings-card is-raised">
              <div className="dash-settings-card-head">
                <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
                  {ICONS[policy.key] ?? <IconFileText size={18} />}
                </span>
                <div className="dash-settings-card-head-copy">
                  <h3>{t('policies.body.title')}</h3>
                  <p>{t('policies.body.noteBefore')} <strong style={{ color: 'var(--dash-text)' }}>{policy.path}</strong>.</p>
                </div>
                <span className={`dash-connect-status is-${live ? 'ok' : 'warn'}`}>
                  {live ? t('policies.live') : exists ? t('policies.unpublished') : t('policies.draft')}
                </span>
              </div>

              {/* Apagar deja el texto que se ve como propio, listo para
                  editarse; encender una con texto propio se confirma. */}
              <AutoSwitch
                policy={policy}
                busy={saving}
                onChange={(on) => {
                  if (dirty) { blockUnsaved(); return }
                  if (on && exists) setConfirmAuto(true)
                  else void persist(on ? { auto: true } : { body: policy.body })
                }}
              />

              {/* Cómo se crea, cuando todavía no existe. La automática va primero
                  porque es la que no se queda vieja: el día que cambian los
                  datos del negocio, el documento ya lo dice. */}
              {!exists && !draft.trim() ? (
                <Notice
                  tone="accent"
                  icon={<IconSparkles size={16} />}
                  title={t('policies.empty.title')}
                  actions={(
                    <>
                      <button type="button" className="dash-primary-btn" disabled={saving} onClick={() => void persist({ auto: true })}>
                        <IconSparkles size={14} /> {t('policies.useAuto')}
                      </button>
                      <button type="button" className="dash-secondary-btn" onClick={() => setDraft(policy.template)}>
                        {t('policies.startTemplate')}
                      </button>
                    </>
                  )}
                >
                  <p>{t('policies.empty.body')}</p>
                </Notice>
              ) : null}

              {policy.auto && !dirty ? (
                <Notice tone="accent" icon={<IconSparkles size={16} />} title={t('policies.auto.title')}>
                  <p>{t('policies.auto.body')}</p>
                </Notice>
              ) : null}

              {/* Guardada y sin publicar por un dato del negocio: el arreglo no
                  está en el texto, está en «Información del negocio». */}
              {exists && !live && !dirty && policy.missingFields.length ? (
                <p className="dash-policy-warn">
                  <IconAlertTriangle size={14} />
                  {t('policies.missingFields', { fields: fieldList(policy.missingFields, t) })}
                  {onOpenBusiness ? <> · <button type="button" className="dash-inline-link" onClick={() => { if (!blockUnsaved()) onOpenBusiness() }}>{t('policies.completeBusiness')}</button></> : null}
                </p>
              ) : null}

              <PolicyVisualEditor value={draft} onChange={setDraft} />

              {pending.length && !(policy.auto && !dirty) ? (
                <p className="dash-policy-warn">
                  <IconAlertTriangle size={14} />
                  {pending.length === 1
                    ? t('policies.fillOne', { list: pending.slice(0, 4).join(', ') })
                    : t('policies.fillMany', { count: pending.length, list: pending.slice(0, 4).join(', ') })}{pending.length > 4 ? '…' : ''}
                </p>
              ) : null}

              <div className="dash-policy-card-foot">
                <span className="dash-field-hint" style={{ margin: 0 }}>
                  {dirty
                    ? t('policies.unsaved')
                    : policy.updatedAt ? t('policies.savedOn', { date: longDate(policy.updatedAt) }) : ''}
                </span>

                {/*
                  El plazo, propuesto y no escrito solo.
                  El servidor sólo lo manda cuando hay un único servicio
                  reservable, que es cuando el número no puede contradecir a
                  ninguna página de reserva. Aun así se ofrece: es una decisión
                  con consecuencia legal, y ponerla sola sería firmar por el
                  dueño una política que no ha elegido.
                */}
                {policy.suggestedNoticeHours !== undefined && draft.includes('[NÚMERO]') ? (
                  <button
                    type="button"
                    className="dash-secondary-btn"
                    onClick={() => setDraft(draft.replace('[NÚMERO]', String(policy.suggestedNoticeHours)))}
                  >
                    <IconSparkles size={14} /> {t('policies.useHours', { hours: policy.suggestedNoticeHours })}
                  </button>
                ) : null}

                {live && siteOrigin ? (
                  <a className="dash-secondary-btn" href={`${siteOrigin}${policy.path}`} target="_blank" rel="noreferrer">
                    <IconExternalLink size={14} /> {t('policies.viewLive')}
                  </a>
                ) : null}
              </div>

              {/* Arriba, junto a las notificaciones, como el resto del panel:
                  el pie de la tarjeta queda debajo de un documento largo y el
                  guardar acababa fuera de la pantalla justo mientras se
                  escribe. */}
              <PendingChangesActions
                dirty={dirty}
                saving={saving}
                onDiscard={() => setDraft(policy.body)}
                onSave={() => void save()}
              />
            </section>

            {confirmAuto ? (
              <ConfirmDialog
                title={t('policies.restoreAuto.title')}
                message={t('policies.restoreAuto.body')}
                confirmLabel={t('policies.restoreAuto.confirm')}
                onConfirm={async () => { await persist({ auto: true }); setConfirmAuto(false) }}
                onCancel={() => setConfirmAuto(false)}
              />
            ) : null}

            <p className="dash-field-hint">
              {t('policies.editorDisclaimer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
